import { reactive } from 'vue';

/**
 * Reactive cache for the Tax Question V2 builder (S3-P2). The live API
 * (`services/taxQuestionV2Api.js`) is the source of truth; this store holds the
 * mapped, UI-shaped copy the V2 builder view / item dialog render and bridges the
 * camelCase DTO ⇄ the request payload, applying the naming canon.
 *
 * V2 is authoring-only (SCOPE FENCE — NO CALC): `FieldKey` is a plain identifier,
 * nothing computes with it. Everything is camelCase end-to-end.
 * R13 narrowed the fence rather than removing it: a CALCULATE item NAMES the Tax Calculate V2
 * formulas to run (and in what order), but nothing here — or anywhere in the questionnaire —
 * evaluates one. The executor does not exist yet; see TaxQuestionV2-ItemKind-Types.md.
 *
 * Naming canon (canonical across S3-P2 ⇄ S3-P3, R1 FieldKey-unified):
 *  - `groupCode`, `stepCode`, `itemKind` → trim + UPPER_SNAKE_CASE.
 *  - `fieldKey` (and INCOME_CLASSIFIER `flagKey`) → trim ONLY (NEVER uppercased —
 *    consumed verbatim as the answer variable name by the S3-P3 node). A FIELD_INPUT
 *    item binds one `fieldKey` chosen from the TaxConfigRuleV2 registry.
 *  - `inputType` / `answerType` are fixed lower-case tokens.
 *
 * §D / blueprint deltas vs V1:
 *  - INCOME_CLASSIFIER rows carry `code`/`flagKey`/`labelTh`/`keywords[]`/`metadata`
 *    ({ assetCategory, propertyType, professionType, businessType } — nulls preserved).
 *  - quick-reply = `answerOptions` chips (string[] or [{label,value}]) + a
 *    `quickReplyPreferConfig` bool (replaces V1's quickReplyEnabled/quickReplyPrompt).
 *    R9: this is the ONLY answer-button list — V1's separate value/label `options`
 *    array was dropped from V2 (nothing at runtime ever read it).
 *  - layered prompts `questionSystemPrompt` (ask) / `answerSystemPrompt` (answer) at both
 *    step and item level (R10 dropped the third `systemPrompt` "general" tier — the POC seed
 *    never authored one and the node never read it).
 *  - `visibleWhen` = a raw DSL string (stored verbatim, fail-open — no builder).
 *  - 6 item kinds (R1, narrowed by R10/R11, then widened by R12/R13): FIELD_INPUT (the single
 *    value-capturing kind, binds one fieldKey) + INCOME_CLASSIFIER + CONFIRM + CONFIRM_SKIP, plus the
 *    two the engine resolves on its own and never asks — SUMMARY (renders a card) and CALCULATE (runs
 *    Tax Calculate V2 formulas, silently; each result lands in `tax.TaxFormV2.CalcResultJson`, never on
 *    an answer row). See TaxQuestionV2-ItemKind-Types.md.
 *
 * @typedef {Object} TaxQuestionVersionV2Dto
 * @property {number} questionVersionID @property {number} taxConfigVersionID @property {string} versionCode
 * @property {?string} description @property {?string} resumePromptTh @property {boolean} isActive
 */

// The 6 V2 item kinds (R1, narrowed by R10/R11, widened by R12 SUMMARY and R13 CALCULATE) — mirror
// backend enum tokens, and the backend whitelist (`TaxQuestionItemV2BAL._itemKinds`) is the one that rejects a save.
// FIELD_INPUT is the single value-capturing kind (replaces RULE_INPUT + PERSONAL_INFO + SPECIAL_INPUT + INCOME_FLAG).
// SUMMARY and CALCULATE are never put to the customer: the walker resolves them and moves on — which is why
// neither appears in SCALAR_KINDS or FIELDKEY_KINDS below.
// 🔴 `CLARIFY` was retired on 2026-07-29 and must not come back. It was a SEPARATE question item that took over
//    when INCOME_CLASSIFIER could not place an income — i.e. the follow-up walked the customer off the very
//    question it was following up on, and the unanswered classifier got recorded as answered. Disambiguation now
//    stays on the question itself, through the turn contract's `needsClarify`.
// 🔴 `DERIVED` was retired on 2026-08-03 and must not come back. It existed only as a "seat" to hang a formula's
//    result on in `tax.TaxFormAnswerV2`, from the mistaken premise that every answer row needs an owning question
//    item. A computed value is not a customer answer: it now lands in `tax.TaxFormV2.CalcResultJson`, keyed by the
//    FieldKey the FORMULA declares (`TaxCalcFunctionV2.FieldKey`), so there is nothing left for the kind to seat.
// SHORT_FORM asks several amounts at once as ONE mini-form card: the card IS the question, so it is asked, it can be
// required, and it carries a bookkeeping FieldKey like the CONFIRM kinds — but the numbers land on the SIBLING
// FIELD_INPUT items its `shortFormConfig` names, never on the SHORT_FORM item itself.
// INCOME_CLASSIFY is NOT a second classifier, despite the name it sits next to. A classifier answers one question
// ("which §40 sections does this person have?") and closes; INCOME_CLASSIFY drives the WHOLE income conversation from
// one item — section, then amount, one customer topic at a time, in the order the customer named them — and stays
// open until every topic is complete. Its catalog therefore carries a MINI SECTION per §40 category (the wording and
// the extraction rules that used to live on the thirteen separate §40 items), plus the fan-out target each answer
// lands on. See TaxPlanningV2-AskOrder-Design.md.
// 🔴 FILING_CHOICE and SIMULATE were MISSING from this file's four kind lists until 2026-08-12, while both were
//    already live in the backend and in the data (question version 7 ships one of each). The editor therefore could
//    not name them, could not show their key, and — through FIELDKEY_KINDS / buildItemRequest below — SENT `null`
//    for a fieldKey the backend requires. Opening such an item and pressing save produced
//    "FILING_CHOICE: ต้องระบุ fieldKey สำหรับเก็บวิธียื่นที่ลูกค้าเลือก (separate|joint)" over a row that had one.
//    Anyone adding a kind to the backend must add it to ALL FOUR lists here plus buildItemRequest's per-kind nulls.
export const ITEM_KINDS = [
  'FIELD_INPUT', 'INCOME_CLASSIFIER', 'INCOME_CLASSIFY', 'CONFIRM', 'CONFIRM_SKIP', 'SUMMARY', 'CALCULATE', 'END_FORM', 'SHORT_FORM',
  'FILING_CHOICE', 'SIMULATE', 'SAVE_STATE', 'MESSAGE', 'USAGE_FUNNEL_STATE',
];
// The two kinds that carry a §40 catalog. Anywhere this list is not consulted, an INCOME_CLASSIFY row is treated as a
// kind with no catalog — and `buildItemRequest` NULLs the catalog of a kind it does not recognise, so a missing member
// here does not merely hide the editor: it DELETES eight sections' worth of authored config on the next save.
export const INCOME_CATALOG_KINDS = ['INCOME_CLASSIFIER', 'INCOME_CLASSIFY'];
export const ITEM_KIND_OPTIONS = [
  { value: 'FIELD_INPUT', title: 'ผูก Field (FIELD_INPUT)' },
  { value: 'INCOME_CLASSIFIER', title: 'จำแนกรายได้ §40 (INCOME_CLASSIFIER)' },
  { value: 'INCOME_CLASSIFY', title: 'คุมบทสนทนารายได้ §40 (INCOME_CLASSIFY)' },
  { value: 'CONFIRM', title: 'จุดยืนยัน/สรุป (CONFIRM)' },
  { value: 'CONFIRM_SKIP', title: 'จุดยืนยันข้อที่ข้าม (CONFIRM_SKIP)' },
  { value: 'SUMMARY', title: 'การ์ดสรุป (SUMMARY)' },
  { value: 'CALCULATE', title: 'คำนวณ (CALCULATE)' },
  { value: 'END_FORM', title: 'จบฟอร์ม (END_FORM)' },
  { value: 'SHORT_FORM', title: 'ฟอร์มสั้น (SHORT_FORM)' },
  { value: 'FILING_CHOICE', title: 'เลือกวิธียื่น แยก/ร่วม (FILING_CHOICE)' },
  { value: 'SIMULATE', title: 'จำลองการลดหย่อน (SIMULATE)' },
  { value: 'SAVE_STATE', title: 'บันทึกสถานะ (SAVE_STATE)' },
  { value: 'MESSAGE', title: 'ข้อความแจ้ง — พูดแล้วเดินต่อ (MESSAGE)' },
  { value: 'USAGE_FUNNEL_STATE', title: 'จุดนับ Usage Funnel (USAGE_FUNNEL_STATE)' },
];
// The kinds the engine resolves on its own — nobody is ever asked one. The JS twin of the backend's
// `TaxQuestionWalkerMapV2.IsNonAskableKind`.
// 🔴 Membership decides `isRequired` in the SAVE PAYLOAD, and the backend does not merely ignore a required
//    non-askable item — it 400s on both ("…ตั้งเป็นข้อบังคับ (isRequired) ไม่ได้ เพราะไม่ได้ถามลูกค้า").
//    A required item the walker steps over would also sit in the progress denominator forever, so the form
//    could never read as complete.
// 🔴 This constant stays even though DERIVED (the kind it was added alongside) is gone: SUMMARY had the very
//    same bug — the payload used to ship `isRequired` for a SUMMARY the backend throws on.
// 🔴 END_FORM is NOT a member, and the distinction is easy to get backwards. It ends the form — but it does so by
//    ASKING something ("shall we start a new plan?") and staying on screen until the answer is clear, so it needs
//    its widget, its chips and its answer path exactly like any other question. What it must not be is `isRequired`,
//    since no answer completes a form that is already Completed; the dialog is what enforces that.
// 🔴 SHORT_FORM is NOT a member either, and it is the easiest one to get wrong: it renders a CARD, which makes it
//    look like a SUMMARY — but the card IS the question. It is asked, it is answered (the customer submits amounts
//    or presses the decline button), and it CAN be `isRequired`. Listing it here would strip `isRequired` out of the
//    save payload and drop the one item the walk was supposed to stop on out of the progress denominator.
// 🔴 SAVE_STATE joined this list on 2026-08-27: like SUMMARY/CALCULATE, the walker resolves it on its own
//    (it snapshots the form under `stateCode` and moves on) — the customer is never asked, so it must not be
//    `isRequired` either, for the exact same reason.
// 🔴 MESSAGE joined on 2026-09-16: the walker SAYS its text (LLM-phrased) and moves on in the same turn, stamping
//    the row so it is said once. A statement, not a question — no fieldKey, no `isRequired`, no chips. Authored
//    last in the set it is the closing line of the questionnaire (the backend's hard-coded completion copy no
//    longer plays in chat once one exists).
// 🔴 USAGE_FUNNEL_STATE joined on 2026-09-25 (#746): a /TaxDashboard funnel marker — the walker logs "this form
//    passed funnel state X" and moves on in the same turn. Asks nothing, says nothing — no fieldKey, no `isRequired`.
export const NON_ASKABLE_KINDS = ['SUMMARY', 'CALCULATE', 'SAVE_STATE', 'MESSAGE', 'USAGE_FUNNEL_STATE'];
// Kinds that carry a widget (inputType/answerType/quick-reply).
// 🔴 Membership here decides what the SAVE PAYLOAD writes, not just what the dialog shows: `buildItemRequest`
//    NULLs inputType/answerType for any kind not listed, so dropping a kind that really does carry them erases
//    both columns on the next save (that is exactly how the two CLARIFY items lost their authored radio/text).
// 🔴 CALCULATE is deliberately ABSENT (same reason SUMMARY is): the customer is never asked it — the walker runs
//    the item's formulas and moves on — so there is nothing to render an input widget for. Listing it would make
//    the save payload write inputType/answerType on an item nobody types into, and the dialog would offer
//    quick-reply chips for a question that is never shown.
// 🔴 SHORT_FORM is deliberately ABSENT for the OPPOSITE reason — not "never asked", but "has no single widget to
//    describe". Its inputs are the N boxes its `shortFormConfig` declares, each one already typed by the sibling
//    FIELD_INPUT that owns it; one item-level inputType/answerType could only ever describe one of them, so the
//    pair stays null and the card's own config is the whole widget spec.
export const SCALAR_KINDS = ['FIELD_INPUT'];
// Kinds that carry a top-level verbatim FieldKey.
// 🔴 INCOME_CLASSIFIER belongs here too, and its absence was the more expensive bug: the classifier's FieldKey
//    (INCOME_TYPES / SPOUSE_INCOME_TYPES) is what names its answer row and what derives its ambiguity marker
//    (`<fieldKey>_ambiguous`, TaxQuestionWalkerMapV2.AmbiguityMarkerKey). Every save wrote null over it, and
//    because the marker falls back to the literal "income_types_ambiguous" when the key is blank, BOTH
//    classifiers then shared one marker — the spouse one would overwrite the main one on a joint form.
// 🔴 SUMMARY is deliberately ABSENT: it stores no answer, and a FieldKey-less item is the one
//    SeedUnansweredDefaults skips. With a key it would seed `<fieldKey> = ""` into the gate map and silently close
//    every `field = value` condition authored below it — the mechanism that once opened 29 spouse questions to a
//    single filer. The backend rejects a SUMMARY carrying a fieldKey outright; this keeps the UI from offering one.
// 🔴 CALCULATE is deliberately ABSENT for the same seeding reason PLUS one of its own: it owns no FieldKey at all.
//    Each result lands on the FieldKey the FORMULA itself declares (`TaxCalcFunctionV2.FieldKey`), so a key on the
//    item would name nothing, seed `<fieldKey> = ""` into the gate map, and close every `field = value` condition
//    below it. The backend rejects a CALCULATE carrying a fieldKey outright; this keeps the UI from offering one.
// 🔴 END_FORM belongs here for the same reason the two CONFIRM kinds do: it stores no tax field, but its answer row
//    needs a name, and the turn contract addresses the focus question BY its FieldKey — a key-less focus gives the
//    extractor nothing to write to, so its AnswerSystemPrompt (the thing that decides "does this mean start over?")
//    could never be read back.
// 🔴 SHORT_FORM belongs here for that same turn-contract reason, and the backend 400s without a key. Its key is a
//    CHECKPOINT ("this screen was answered"), never one of the amounts — the numbers land on the sibling FIELD_INPUT
//    items its `shortFormConfig` names. Reusing a member fieldKey as the item's own is rejected on save, because the
//    two writes would race in one turn and whichever landed last would decide both the amount and the checkpoint.
// 🔴 INCOME_CLASSIFY belongs here for the SHORT_FORM reason, not the INCOME_CLASSIFIER one: its key is a CHECKPOINT
//    ("this income conversation is settled"), never one of the amounts — those land on the sibling §40 FIELD_INPUT
//    rows its catalog names. The backend 400s without a key, and the turn contract addresses the focus question by
//    its FieldKey, so a key-less driver gives the extractor nothing to aim at.
// 🔴 FILING_CHOICE and SIMULATE belong here for DIFFERENT reasons, and neither is optional on the backend:
//    FILING_CHOICE's key holds a REAL VALUE (separate|joint) that every later calculation reads, and SIMULATE's
//    is a checkpoint the scenario lives on. buildItemRequest nulls the key of any kind absent from this list.
export const FIELDKEY_KINDS = ['FIELD_INPUT', 'INCOME_CLASSIFIER', 'INCOME_CLASSIFY', 'CONFIRM', 'CONFIRM_SKIP', 'END_FORM', 'SHORT_FORM',
  'FILING_CHOICE', 'SIMULATE'];

/// The kinds that persist `cardConfigJson`. 🔴 Same shape of trap as FIELDKEY_KINDS: a kind missing here has its
//  config silently dropped by buildItemRequest on every save.
export const CARDCONFIG_KINDS = ['SUMMARY', 'FILING_CHOICE', 'SIMULATE'];

// The kinds that persist `stateCode` (the named save-slot a SAVE_STATE item writes, and the slot SUMMARY /
// SIMULATE read back to render that saved snapshot). 🔴 Single source of truth — declared HERE ONLY; any other
// file (dialog, edit view) that needs this list must `import { STATECODE_KINDS } from './taxQuestionV2Store'`
// rather than re-typing the three tokens, or the two copies will drift the exact way FIELDKEY_KINDS/CARDCONFIG_KINDS
// warn about above. Same shape of trap: a kind missing here has `stateCode` silently NULLed by buildItemRequest
// on every save.
// 🔴 FILING_CHOICE is deliberately ABSENT even though it is a CARDCONFIG_KINDS member. Its card IS the question
//    (the customer picks separate|joint) — replaying it against a saved state on resume would ask the customer
//    to choose again, which is exactly the "ask a question twice" bug this file's other kind-list comments warn
//    about. `stateCode` only belongs on items that read or write a snapshot silently, never on one the customer
//    answers.
export const STATECODE_KINDS = ['SAVE_STATE', 'SUMMARY', 'SIMULATE'];

// The kinds that persist the RESUME STAGE-2 copy — `confirmTextTh` / `confirmOptions` / `confirmSystemPrompt`,
// the "this is what you saved — use it, or update it?" beat a returning customer gets AFTER tapping «ทำต่อ» and
// BEFORE the next real question is asked.
// 🔴 Single source of truth — declared HERE ONLY, exactly like STATECODE_KINDS above: any other file (dialog,
//    edit view) that needs this list must `import { STATE_CONFIRM_KINDS } from './taxQuestionV2Store'` rather
//    than re-typing the token. Same closed-triangle trap too: a kind missing here has all three fields silently
//    NULLed by buildItemRequest on every save.
// 🔴 DELIBERATELY NARROWER THAN STATECODE_KINDS (SAVE_STATE only). SUMMARY / SIMULATE merely NAME the slot they
//    belong to — the SAVE_STATE pin is the item a resume turn is looked UP BY, so it is the only row the stage-2
//    copy can be read from. A card carrying its own confirm text would be a second script nobody reads.
// 🔴 Blank `confirmTextTh` IS the off switch: no stage 2 at all, resume jumps straight to the next real question
//    exactly as it did before these fields existed (no data migration, no boolean flag).
// 🔴 This list ALSO fences the two pin-only ticks: `confirmSkipToQuestion` and `postCompleteEditReturn` («หลังแก้
//    แผนที่จบแล้ว ให้กลับมาที่ฉากนี้» — the pin a post-completion edit round walks back to). Both are sent `false`
//    (never null — the columns are NOT NULL) for every kind outside this list.
export const STATE_CONFIRM_KINDS = ['SAVE_STATE'];

// The kinds that persist `editKeywords` («คำที่ลูกค้าใช้เรียกข้อนี้ (จับคำขอแก้)») — the words a customer types when
// asking to change THIS item ("ขอแก้วิธียื่น", "ขอเปลี่ยนเป็นแยกยื่น"). The edit-request matcher counts every one of
// them as a name of the item (same weight as its Label), and they ride to the edit-request LLM as a hint.
// = every kind that can be the TARGET of an edit request: the askable kinds the edit scope keeps, plus SHORT_FORM
//   (targeted as a whole card). The JS twin of the backend's `TaxQuestionItemV2BAL.EditKeywordKinds` — keep the
//   two in step.
// 🔴 Single source of truth — declared HERE ONLY, exactly like STATE_CONFIRM_KINDS above: the dialog must import
//    it. Same closed-triangle trap: a kind missing here has its keywords sent as `[]` by buildItemRequest on every
//    save (the backend stores NULL), i.e. silently wiped.
// 🔴 Not the menu text: the «จะแก้รายการไหน» menu still prints the Label. These words only help the matcher find
//    the item.
// SUMMARY: คำเรียกใช้ดึงการ์ดสรุปมาแสดง (ทาง recall ของโหนด) ไม่ใช่เป้าของการขอแก้ — ลูกค้าพิมพ์คำนั้น (ไม่มีคำว่า
//    แก้/เปลี่ยน/ปรับ และไม่พกค่ามา) ขณะคำถามอื่นค้างจอ ⇒ โหนดแนบการ์ดสรุปใบนั้น (เฉพาะใบที่ walk เคยผ่านแล้ว) มากับ
//    เทิร์นนี้ แล้วถามข้อที่ค้างต่อ · ตัวจับคำขอแก้ไม่เห็นคำเหล่านี้ (SUMMARY ไม่อยู่ใน scope ขอแก้) · same backend
//    allow-list, so the JS twin rule above still holds.
export const EDIT_KEYWORD_KINDS = ['FIELD_INPUT', 'INCOME_CLASSIFIER', 'INCOME_CLASSIFY', 'SHORT_FORM', 'FILING_CHOICE', 'SUMMARY'];

// The kinds that persist `stateGroup` — the App Bar group state (the "Pending in Specific State" condition) a form
// is counted under once the walker passes this pin. The four values come from `api/v1/BarManagement/options` →
// `conditionStateOptions`; 🔴 never type them into the web page.
// 🔴 Single source of truth — declared HERE ONLY, exactly like STATE_CONFIRM_KINDS above: the dialog must import
//    it. Same closed-triangle trap: a kind missing here has `stateGroup` silently NULLed by buildItemRequest.
// 🔴 Blank IS "leave the form's group alone": the walker writes nothing, it does not clear the group.
// 🔴 USAGE_FUNNEL_STATE persists `stateGroup` too (#746) — the SAME column holding a DIFFERENT closed vocabulary: its
//    funnel state (USAGE_FUNNEL_STATE_OPTIONS below), required. The App Bar dropdown stays SAVE_STATE-only in the
//    dialog; this list only decides what buildItemRequest sends.
export const STATEGROUP_KINDS = ['SAVE_STATE', 'USAGE_FUNNEL_STATE'];

// The funnel states a USAGE_FUNNEL_STATE item can mark — the /TaxDashboard "Usage Funnel by Form" rows from
// "Completed Income Input" to "Recommend", in funnel order (#746). The JS twin of the backend's
// `UsageFunnelState` (Model/AppModel/TaxSectionLogModels.cs) — keep the two in step. Titles = the dashboard row names.
export const USAGE_FUNNEL_STATE_OPTIONS = [
  { value: 'COMPLETED_INCOME_INPUT', title: 'Completed Income Input' },
  { value: 'CONFIRM_INCOME_EXPENSE', title: 'Confirm Income&Expence' },
  { value: 'COMPLETED_DEDUCTION_INPUT', title: 'Completed Deduction Input' },
  { value: 'CONFIRM_ALLOWANCE', title: 'Confirm Allowance' },
  { value: 'COMPLETED_TAX_FORM', title: 'Completed Tax Form' },
  { value: 'COMPLETE_SUGGESTION', title: 'Complete Suggestion' },
  { value: 'COMPLETE_SIMULATION', title: 'Complete Simulation' },
  { value: 'RECOMMEND', title: 'Recommend' },
];

export const INPUT_TYPES = ['number', 'radio', 'text'];
export const INPUT_TYPE_OPTIONS = [
  { value: 'number', title: 'ตัวเลข (number)' },
  { value: 'radio', title: 'ตัวเลือก (radio)' },
  { value: 'text', title: 'ข้อความ (text)' },
];
// ── How an item is BADGED on screen ──────────────────────────────────────────────────────────────
// 🔴 Both maps live here, not in the screen that draws the table, because the item list is no longer the only place
//    an item is shown: an INCOME_CLASSIFY's dialog previews the §40 rows its catalog points at, and an author reads
//    those two views against each other. A row badged `primary/ตัวเลข` in one and `grey/—` in the other reads as two
//    different items.
export const KIND_COLORS = {
  FIELD_INPUT: 'primary',
  INCOME_CLASSIFIER: 'warning',
  INCOME_CLASSIFY: 'deep-orange',
  CONFIRM: 'teal',
  CONFIRM_SKIP: 'indigo',
  SUMMARY: 'teal',
  CALCULATE: 'deep-purple',
  END_FORM: 'error',
  SHORT_FORM: 'cyan',
  FILING_CHOICE: 'blue-grey',
  SIMULATE: 'purple',
  SAVE_STATE: 'brown',
  MESSAGE: 'light-green',
  USAGE_FUNNEL_STATE: 'blue',
};

// Readable answer-kind tag for a FIELD_INPUT row, keyed by the V2 inputType token
// (R10 dropped controlType — inputType is the only widget signal). Mirrors V1's
// ANSWER_KIND_META shape { label(TH), color, icon(mdi), hint(TH tooltip) }.
export const ANSWER_KIND_META = {
  number: { label: 'ตัวเลข', color: 'success', icon: 'mdi-numeric', hint: 'กรอกตัวเลข / จำนวน' },
  radio: { label: 'ตัวเลือก', color: 'secondary', icon: 'mdi-radiobox-marked', hint: 'เลือก 1 ตัวเลือก' },
  text: { label: 'ข้อความ', color: 'secondary', icon: 'mdi-form-textbox', hint: 'พิมพ์ข้อความอิสระ' },
};

export const ANSWER_TYPES = ['number', 'text'];
export const ANSWER_TYPE_OPTIONS = [
  { value: 'number', title: 'ตัวเลข (number)' },
  { value: 'text', title: 'ข้อความ (text)' },
];
// Free-text VisibleWhen grammar hints (V2 = raw DSL field, fail-open).
export const VISIBLE_WHEN_HINTS = [
  'Always',
  'marital_status = married',
  'type_40_5 = yes',
  'marital_status in MARRIED,WIDOWED',
  'has_income = true AND spouse_has_income = yes',
  'type_40_1 = yes OR type_40_2 = yes',
];

const GROUP_LABELS = {
  PERSONAL: 'ข้อมูลส่วนตัว',
  INCOME: 'รายได้',
  DEDUCTION: 'ค่าลดหย่อน',
  SUMMARY: 'สรุป',
  GENERAL: 'ทั่วไป',
};

let uidSeq = 1;
function nextUid() {
  uidSeq += 1;
  return uidSeq;
}

/**
 * localStorage key holding this builder's UI preferences — currently the single
 * `showCardPreview` switch on the page header.
 *
 * 🔴 PREFERENCES ARE NOT DOCUMENT STATE. Nothing in this blob may ever reach a save
 * payload or a diff: `buildItemRequest()` reads the item rows, `tqContentSig()` hashes
 * them, and the builder's `dirty` flag is computed from that hash. A view preference
 * that leaked onto an item (or into the request) would make merely LOOKING at the page
 * count as an unsaved edit and pop the leave-guard confirm. It lives here, beside the
 * cache, precisely so it stays off the rows.
 */
export const PREFS_KEY = 'taxQuestionV2.prefs';

const state = reactive({
  /** @type {?number} currently selected Tax Config Version (FK) */
  configVersionId: null,
  /** @type {TaxQuestionVersionV2Dto[]} question versions of the selected config version */
  versions: [],
  /** @type {?TaxQuestionVersionV2Dto} version currently open in the builder */
  currentVersion: null,
  /** @type {Object[]} flat, ordered working copy of items in the builder */
  items: [],
  /**
   * Per-step/section metadata keyed by UPPER groupCode (== stepCode). Single source
   * the builder edits inline and serializes to the bulk-save `steps[]` payload.
   * @type {Object<string,Object>}
   */
  groupMeta: {},
  /** @type {string[]} non-blocking orphan-FieldKey warnings from getFull / bulk-save */
  warnings: [],
  /**
   * UI preference (persisted): draw the real card preview inline under every card-kind
   * row of the builder table. Default OFF — each preview is its own POST to
   * `card/preview` (no batch endpoint), so the page must cost nothing until asked.
   * @type {boolean}
   */
  showCardPreview: false,
});

/**
 * Read the persisted preferences. Private-browsing mode, a full quota or a corrupt
 * blob must never take the builder down — the defaults stay usable, silently.
 */
function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return;
    const p = JSON.parse(raw);
    if (!p || typeof p !== 'object') return;
    // 🔴 `=== true`, not `Boolean(p.x)` and not `p.x ?? false`: a blob written before this
    // switch existed has no such key, and a corrupt one may hold a string — both must read
    // as OFF. `Boolean('false')` is `true`, which would turn the previews on for admins who
    // never asked and fire one request per card row on first paint.
    state.showCardPreview = p.showCardPreview === true;
  } catch (e) {
    // unreadable prefs are not an error worth surfacing — fall back to defaults
  }
}

/** Persist ONLY the UI preferences. Never items / groupMeta / warnings — those are the document. */
function savePrefs() {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify({
      showCardPreview: state.showCardPreview,
    }));
  } catch (e) {
    // quota / private mode — preferences are a convenience, not state we depend on
  }
}

/** Toggle the inline card previews. 🔴 Must not touch items — see PREFS_KEY on why. */
function setShowCardPreview(on) {
  state.showCardPreview = on === true;
  savePrefs();
}

// Read once, at module init: this store is a module-level singleton, so the first view that
// imports it already sees the admin's last choice — no per-view load call to forget.
loadPrefs();

function trimUpper(value) {
  return String(value ?? '').trim().toUpperCase();
}
function trimOnly(value) {
  return String(value ?? '').trim();
}
/**
 * The three members of a card-copy language bag, spelled the way the backend reads them
 * (`TaxCardLangV2` — th/en/zh, never "cn").
 */
export const CARD_LANGS = ['th', 'en', 'zh'];

/** Is this value a language bag `{th,en,zh}` rather than a plain string? Mirrors TaxCardLangV2.IsBag. */
export function isLangBag(value) {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  if (keys.length === 0) return false;
  return keys.every((k) => CARD_LANGS.includes(String(k).toLowerCase())
    && (value[k] == null || typeof value[k] === 'string'));
}

/** The Thai reading of a copy value, whichever shape it is in — what every plain input box binds to. */
export function langTh(value) {
  return isLangBag(value) ? trimOnly(value.th) : trimOnly(value);
}

/**
 * 🔴 THE ONE CHOKE POINT THAT KEEPS TRANSLATIONS ALIVE.
 *
 * Card copy may be a plain string (every card authored before three languages existed) or a language bag
 * `{th,en,zh}`. `trimOnly` cannot be used on it: `String({th:'ก'})` is the literal `'[object Object]'`, which
 * is not a lost translation but a GARBAGE STRING that then renders to the customer — and it happens on LOAD as
 * well as on save, so the corruption would appear before anyone typed anything. It is also version-wide: the
 * builder's Save rebuilds EVERY item, so editing one unrelated question would have destroyed the bags on all
 * eleven mini-forms.
 *
 * Bags come back trimmed with blank members dropped (blank = not authored, the same rule the backend applies);
 * a bag left with nothing in it collapses to `''` so the existing null/blank tests keep working unchanged.
 */
export function langText(value) {
  if (!isLangBag(value)) return trimOnly(value);
  const bag = {};
  CARD_LANGS.forEach((k) => {
    const text = trimOnly(value[k] ?? value[k.toUpperCase()]);
    if (text) bag[k] = text;
  });
  return Object.keys(bag).length ? bag : '';
}

/** langText → null when nothing is authored in any language (the note box's "leave the key out" rule). */
function nzLang(value) {
  const out = langText(value);
  return out === '' ? null : out;
}
/** Trim → null-if-empty (Thai-safe: only ends trimmed). */
function nz(value) {
  const t = trimOnly(value);
  return t ? t : null;
}
/**
 * A number, or null when the author left the box empty.
 * 🔴 `Number('')` is 0, not NaN — the one coercion that turns "no ceiling authored" into
 * "ceiling of zero" and pins a stepper's + button on the first press.
 */
function numOrNull(value) {
  if (value === null || value === undefined || trimOnly(value) === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function groupLabel(groupCode) {
  const code = trimUpper(groupCode);
  return GROUP_LABELS[code] || code || 'ทั่วไป';
}

/** Trim + dedupe a string array (optionally transforming each value first). */
function uniqueStrings(values, mapFn) {
  if (!Array.isArray(values)) return [];
  const out = [];
  const seen = new Set();
  values.forEach((v) => {
    const s = String((mapFn ? mapFn(v) : v) ?? '').trim();
    if (s && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  });
  return out;
}

// ── version cache mutators ─────────────────────────────────────────────────
function setConfigVersion(id) {
  state.configVersionId = id != null ? Number(id) : null;
}
function setVersions(dtoArray) {
  state.versions = Array.isArray(dtoArray) ? dtoArray.slice() : [];
}
function upsertVersion(dto) {
  if (!dto || dto.questionVersionID == null) return;
  const idx = state.versions.findIndex((v) => v.questionVersionID === dto.questionVersionID);
  if (idx >= 0) state.versions.splice(idx, 1, { ...state.versions[idx], ...dto });
  else state.versions.push({ ...dto });
}
function removeVersion(questionVersionID) {
  const idx = state.versions.findIndex((v) => v.questionVersionID === questionVersionID);
  if (idx >= 0) state.versions.splice(idx, 1);
}
/** Single-active-per-config-version optimistic reflection. */
function reflectVersionActive(questionVersionID, isActive) {
  const target = state.versions.find((v) => v.questionVersionID === questionVersionID);
  if (!target) return;
  if (isActive) {
    state.versions.forEach((v) => {
      if (v.taxConfigVersionID === target.taxConfigVersionID) {
        v.isActive = v.questionVersionID === questionVersionID;
      }
    });
  } else {
    target.isActive = false;
  }
}

// ── item mappers ───────────────────────────────────────────────────────────
/**
 * Normalize answerOptions chips.
 *
 * 🔴 TWO RULES, BOTH ABOUT NOT INVENTING DATA.
 *
 * 1. UNKNOWN KEYS SURVIVE. This used to rebuild every row as a fresh `{label, value}` literal, which
 *    silently deleted any other member the row carried — and the per-language labels (`labelEn` /
 *    `labelZh`) are exactly that: keys this file has no opinion about. A save from the dialog would
 *    have wiped a translation the admin entered one screen earlier. The row is spread, then only the
 *    members this function actually owns are overwritten.
 *
 * 2. `value` IS NEVER SYNTHESISED FROM `label`. A chip in this product is a LABEL and carries no
 *    hidden code — the stored answer comes from the item's AnswerSystemPrompt, not from the button.
 *    Copying the label into `value` looked harmless while every row was a bare string, but the moment
 *    rows are promoted to objects it makes `FormatOptions` start printing `[ตัวเลือก: …]` into the
 *    prompt of questions whose vocabulary already lives there. `value` is emitted only when the
 *    SOURCE had a non-empty one.
 *
 * Bare strings are promoted to `{label}` — same information, and the only shape that has somewhere to
 * put a translation.
 *
 * The PascalCase spellings (`Label` / `Value` / `LabelEn` / `LabelZh`) are read as well, because rows
 * saved by older builds use them; the per-language ones are rewritten to their camelCase name.
 */
function normalizeAnswerOptions(values) {
  if (!Array.isArray(values)) return [];
  const out = [];
  values.forEach((v) => {
    if (v == null) return;
    if (typeof v === 'object') {
      const label = trimOnly(v.label ?? v.Label ?? v.value ?? v.Value ?? '');
      const value = trimOnly(v.value ?? v.Value ?? '');   // never synthesised from the label — see rule 2
      if (!label && !value) return;
      const row = { ...v, label };
      if (value) row.value = value;
      else delete row.value;      // an empty string here is not "no value", it is a value of ''

      // Canonicalize the multilingual fields when they are present. The dialog creates these keys for
      // every row, while this also preserves rows loaded from older data that used PascalCase.
      if (Object.prototype.hasOwnProperty.call(v, 'labelEn') || Object.prototype.hasOwnProperty.call(v, 'LabelEn')) {
        row.labelEn = trimOnly(v.labelEn ?? v.LabelEn ?? '');
        delete row.LabelEn;
      }
      if (Object.prototype.hasOwnProperty.call(v, 'labelZh') || Object.prototype.hasOwnProperty.call(v, 'LabelZh')) {
        row.labelZh = trimOnly(v.labelZh ?? v.LabelZh ?? '');
        delete row.LabelZh;
      }

      out.push(row);
    } else {
      const s = trimOnly(v);
      if (s) out.push({ label: s });
    }
  });
  return out;
}

/** Normalize an INCOME_CLASSIFIER metadata block, preserving null members. */
function normalizeMetadata(m) {
  if (m == null || typeof m !== 'object') return null;
  const pick = (k) => {
    const v = m[k];
    if (v == null) return null;
    const s = String(v).trim();
    return s ? s : null;
  };
  return {
    assetCategory: pick('assetCategory'),
    propertyType: pick('propertyType'),
    professionType: pick('professionType'),
    businessType: pick('businessType'),
  };
}

/** Normalize one category's sub-type list. A formula switches on `code`, so it is trimmed and never uppercased. */
function mapIncomeSubTypes(list) {
  if (!Array.isArray(list)) return null;
  const out = list
    .map((s) => ({ code: trimOnly(s && s.code), labelTh: trimOnly(s && s.labelTh) }))
    .filter((s) => s.code && s.labelTh);
  return out.length ? out : null;
}

/**
 * @param {*} list raw category rows off the dialog
 * @param {boolean} withWiring include the INCOME_CLASSIFY-only members — where each section's answer is written and
 *   which sub-types it offers. FALSE for an INCOME_CLASSIFIER, whose catalog only ever said WHICH sections exist:
 *   omitting them keeps that kind's IncomeCategoriesJson byte-identical to what it has always written, and stops a
 *   fan-out pointer surviving on an item that no longer fans anything out.
 */
function mapIncomeCategories(list, withWiring) {
  if (!Array.isArray(list)) return null;
  return list
    .map((c) => {
      const row = {
        code: trimOnly(c.code),
        labelTh: trimOnly(c.labelTh),
        // flagKey stays verbatim (trim only — consumed as the answer variable name).
        flagKey: trimOnly(c.flagKey),
        keywords: uniqueStrings(c.keywords, (v) => String(v ?? '')),
        metadata: normalizeMetadata(c.metadata),
      };
      if (!withWiring) return row;
      // 🔴 Pointers ONLY. The Thai question and the extraction rules stay on the §40 item these name — that item is
      //    what an admin opens, what every VisibleWhen reads, and what asks the same question when the dialog is not
      //    driving. A copy here used to outrank it silently, so editing the visible question changed nothing.
      row.incomeFieldKey = trimOnly(c.incomeFieldKey) || null;
      row.periodFieldKey = trimOnly(c.periodFieldKey) || null;
      row.subTypes = mapIncomeSubTypes(c.subTypes);
      return row;
    })
    .filter((c) => c.code && c.flagKey && c.labelTh);
}

/**
 * Normalize a CALCULATE item's config into a shape the dialog can bind to without null checks:
 * `{ calcVersionId: ?number, functions: [{ functionId, functionCode }] }`.
 *
 * 🔴 This lives HERE, exported, because the dialog needs the exact same shape — declaring a second copy next to
 *    the dialog is how `SCALAR_KINDS`/`FIELDKEY_KINDS` once drifted and the payload started dropping what the user
 *    had typed (see the 🔴 note in TaxQuestionItemV2Dialog.vue).
 *
 * Rules:
 *  - accepts null / undefined / a JSON string (older rows or a raw column read) / an object; never throws.
 *  - `functions` is ALWAYS an array; a row is dropped only when it carries NEITHER a `functionId` NOR a
 *    `functionCode` — such a row resolves to nothing anywhere.
 *    🔴 A row with a code but no usable id is KEPT (functionId falls back to 0). `functionId` is the identity of a
 *    ROW IN ONE DATABASE: after a calc set is carried to UAT/PRD the numbers do not line up, and dropping those
 *    rows here made the formulas vanish from the dialog before the request was even built — the "หาสูตรไม่เจอ
 *    หลัง migrate" report of 5 Sep 2026. The backend resolves by code first (ValidateCalcConfigAsync) and rewrites
 *    the id to this database's, so a code-only row saves cleanly.
 *  - `functionCode` is trimmed ONLY — NEVER uppercased. It is the C# function name and the executor will resolve
 *    by code first (a calc-version clone regenerates FunctionID but keeps FunctionCode), so it must stay verbatim.
 *  - ORDER IS PRESERVED — the list order IS the run order. Never sort, never dedupe here (the backend 400s on a
 *    duplicate functionId; silently collapsing it would hide the authoring mistake).
 *
 * @param {?(Object|string)} raw
 * @returns {{ calcVersionId: ?number, functions: {functionId: number, functionCode: string}[] }}
 */
export function normalizeCalcConfig(raw) {
  let src = raw;
  if (typeof src === 'string') {
    const text = src.trim();
    if (!text) return { calcVersionId: null, functions: [] };
    try {
      src = JSON.parse(text);
    } catch {
      return { calcVersionId: null, functions: [] };
    }
  }
  if (src == null || typeof src !== 'object' || Array.isArray(src)) {
    return { calcVersionId: null, functions: [] };
  }
  const versionId = Number(src.calcVersionId);
  const functions = [];
  (Array.isArray(src.functions) ? src.functions : []).forEach((f) => {
    if (f == null || typeof f !== 'object') return;
    const rawId = Number(f.functionId);
    const functionId = Number.isFinite(rawId) && rawId > 0 ? rawId : 0;
    const functionCode = trimOnly(f.functionCode);
    if (functionId === 0 && !functionCode) return;      // ไม่มีทั้ง id และ code ⇒ ชี้ไปที่ไหนไม่ได้เลย
    functions.push({ functionId, functionCode });
  });
  return {
    calcVersionId: Number.isFinite(versionId) && versionId > 0 ? versionId : null,
    functions,
  };
}

// Wire defaults for the SHORT_FORM card — the JS twin of `TaxShortFormCardV2`'s Default* constants. Kept in sync by
// hand: the backend fills the very same values when a field is blank, so a config authored here and a config saved
// by an older client render identically.
/**
 * How a mini-form row may be DRAWN. '' (absent on the wire) is the money box every deduction card uses; the two
 * below are what the ALLOWANCE cards need — a counter for people, a tick for one person who qualifies or does not.
 * 🔴 Mirrors `TaxShortFormCardV2.StepperControl` / `CheckboxControl`. A value not in this list is dropped rather
 * than stored, so a typo cannot reach the app as a control it does not know how to draw.
 */
export const SHORT_FORM_CONTROLS = ['STEPPER', 'CHECKBOX'];

const SHORT_FORM_DEFAULTS = {
  cardType: 'TAX_AMOUNT_FORM',
  refId: 'type7',
  version: 1,
  submitLabel: 'บันทึกและดำเนินการต่อ',
  secondaryLabel: 'ไม่มีรายการเหล่านี้',
  placeholder: '0',
};

/** One blank input row for the dialog's "add field" button (never bound to `undefined`). */
export function blankShortFormField() {
  return {
    fieldKey: null,
    label: '',
    placeholder: SHORT_FORM_DEFAULTS.placeholder,
    noteBelow: '',
    noteAbove: '',
    // The unit printed after THIS box — per row, because one card mixes baht boxes with a "คน" counter and a
    // checkbox that has no unit at all. Seeded with 'บาท' because the money box is what almost every new row is;
    // clearing it is a one-keystroke edit and a cleared one ships no `unitLabel` key at all (same three states
    // as `note`). 🔴 A row-level seat — the card-wide `data.unitLabel` it replaces is GONE, not renamed.
    unitLabel: 'บาท',
    sameGroupAsPrevious: false,
    // '' = the money box every deduction card uses; 'STEPPER' = a − n + counter (people, not baht).
    control: '',
    min: null,
    max: null,
    step: null,
    // null, not {} — "this row was never styled". An empty bag would be stored, diffed and re-read forever as a
    // key nobody wrote, and it merges to exactly the same card.
    style: null,
  };
}

/**
 * Normalize a SHORT_FORM item's config into a shape the dialog can bind to without null checks:
 * `{ cardType, refId, version, titleTh, subtitleTh, submitLabel, secondaryLabel, fields: [...] }`.
 * 🔴 There is no card-wide `unitLabel` any more: the unit is a per-row seat (`fields[].unitLabel`), because one
 *    card mixes baht boxes with a people counter and a checkbox that carries no unit.
 *
 * 🔴 Same reason `normalizeCalcConfig` lives here and is exported: the dialog needs the identical shape, and a
 *    second copy declared next to the dialog is exactly how the payload starts dropping what the user typed.
 *
 * Rules:
 *  - accepts null / undefined / a JSON string (a raw column read) / an object; never throws.
 *  - `fields` is ALWAYS an array and ORDER IS PRESERVED — the list order IS the display order, and
 *    `sameGroupAsPrevious` is read against the row physically before it, so a sort would re-draw the separators.
 *  - rows with no fieldKey are KEPT here (a row the author has not picked a key for yet is still being edited);
 *    dropping them is the SAVE step's job — see `buildShortFormConfigRequest`.
 *  - `noteBelow` / `noteAbove` are normalized to '' rather than null so a v-model textarea binds to them; the save
 *    step turns a blank one back into null, which is what leaves the note box off the wire payload entirely.
 * 🔴 `secondaryLabel` is the one field where null and '' mean DIFFERENT things (mirrors `TaxShortFormCardV2.Normalize`):
 *    null/undefined = "the author said nothing" → the default caption; '' = "the author removed the decline button"
 *    and must survive as ''. Coercing '' to the default here would resurrect a button someone deliberately deleted.
 *
 * @param {?(Object|string)} raw
 * @returns {Object}
 */
/**
 * The card-level names this mapper OWNS — everything it writes, plus the legacy spellings it READS.
 *
 * 🔴 The legacy names are in here for a reason that is not obvious: `carryUnknown` below would otherwise
 * preserve `titleTh` beside the `title` it was just collapsed into, and the backend's `TaxCardLangV2` renames
 * `titleTh` → `title` on the way in — so the document would arrive carrying the same seat twice, with the stale
 * copy able to win. A legacy key is not an unknown key; it is a key that has already been read.
 */
const SHORT_FORM_CARD_KEYS = [
  'cardType', 'refId', 'version', 'title', 'subtitle', 'submitLabel', 'secondaryLabel', 'style', 'fields',
  'titleTh', 'subtitleTh',
];

/** The row-level twin of `SHORT_FORM_CARD_KEYS`. Same rule, same reason for the two `…Th` entries. */
const SHORT_FORM_ROW_KEYS = [
  'fieldKey', 'label', 'placeholder', 'noteBelow', 'noteAbove', 'unitLabel', 'sameGroupAsPrevious',
  'control', 'min', 'max', 'step', 'style',
  // The legacy spellings. Named here so they are NOT carried through as unknown extras: the mapper reads them
  // into their new seat (`labelTh` → `label`, `note`/`noteTh` → `noteBelow`) and a copy left beside the new key
  // would be saved back and re-read forever.
  'labelTh', 'note', 'noteTh',
];

/**
 * Copy every key of `src` this mapper does not name onto `target`, in place.
 *
 * 🔴 WHAT THIS FIXES. `normalizeShortFormConfig` REBUILDS the config key by key, so until now a mini-form config
 * could only ever hold the keys this file happened to list: an admin who hand-authored `theme`, `cornerRadius`,
 * an icon — or any hint the app added after this file was last edited — watched it disappear on the next save,
 * silently, with no error anywhere. The SUMMARY card never had that problem because its config is stored as an
 * opaque string; this is the mini-form catching up.
 *
 * 🔴 A NAMED KEY ALWAYS WINS. The list above is what keeps normalization meaningful — `control` is canonicalised,
 * bounds belong to the counter alone, a blank note becomes null — so an unknown key may only ever ADD to the
 * document, never take a seat this mapper decided.
 *
 * 🔴 Values are carried VERBATIM, unparsed and unvalidated. This file cannot know what the app will do with a key
 * it has never heard of, and guessing would be a second opinion that goes stale — the same rule the backend's
 * `TaxCardStyleV2` follows for exactly the same reason.
 */
function carryUnknown(target, src, claimed) {
  if (!src || typeof src !== 'object' || Array.isArray(src)) return target;
  Object.keys(src).forEach((key) => {
    if (claimed.includes(key)) return;
    if (Object.prototype.hasOwnProperty.call(target, key)) return;
    target[key] = src[key];
  });
  return target;
}

export function normalizeShortFormConfig(raw) {
  let src = raw;
  if (typeof src === 'string') {
    const text = src.trim();
    if (!text) return blankShortFormConfig();
    try {
      src = JSON.parse(text);
    } catch {
      return blankShortFormConfig();
    }
  }
  if (src == null || typeof src !== 'object' || Array.isArray(src)) return blankShortFormConfig();

  const version = Number(src.version);
  const fields = [];
  (Array.isArray(src.fields) ? src.fields : []).forEach((f) => {
    if (f == null || typeof f !== 'object') return;
    fields.push(carryUnknown({
      // fieldKey verbatim (trim only — never uppercased): it names a sibling FIELD_INPUT item and ships to the app
      // as the input's `id`, which is the key a submitted amount comes back on.
      fieldKey: nz(f.fieldKey),
      // 🔴 langText, not trimOnly — see its comment. A bag here is a translated row, not a value to stringify.
      // 🔴 `?? f.labelTh` is the legacy name. The seat lost its `Th` when it stopped being Thai-only; documents
      //    stored before that still carry the old key, and the backend accepts both (TaxCardLangV2.LegacyKeys).
      label: langText(f.label ?? f.labelTh),
      placeholder: langText(f.placeholder) || SHORT_FORM_DEFAULTS.placeholder,
      // 🔴 `?? f.note ?? f.noteTh` are the legacy names, oldest last. The seat was called `note` while the wire
      //    and the config shared one name; the config now says `noteBelow` so the pair `noteAbove`/`noteBelow`
      //    answers "where does this land" by itself, while the WIRE still says `note` (the phone reads that key —
      //    see the DTO). Documents stored before the rename still carry the old key and the backend accepts both.
      noteBelow: langText(f.noteBelow ?? f.note ?? f.noteTh),
      // The caption ABOVE the box — a second seat, not a re-reading of `noteBelow`. See the DTO's own remark.
      noteAbove: langText(f.noteAbove),
      // 🔴 The ROW's unit (not the card's — that seat no longer exists). No default is applied here on purpose:
      //    a blank one must stay blank so a CHECKBOX/STEPPER row never inherits "บาท". `blankShortFormField`
      //    seeds the convenience default at the moment a row is ADDED; everything else keeps what was authored.
      unitLabel: langText(f.unitLabel),
      sameGroupAsPrevious: Boolean(f.sameGroupAsPrevious),
      // 🔴 Read back or LOST. This mapper REBUILDS the row, so a key it does not name is dropped the next time
      //    anyone saves this item — the counter would silently become a text box, on a card whose whole point is
      //    that it counts people. Bounds are numbers or null; '' from an emptied input must not become 0, which
      //    would pin a "+" button at a ceiling nobody authored.
      control: SHORT_FORM_CONTROLS.includes(String(f.control || '').trim().toUpperCase())
        ? String(f.control).trim().toUpperCase()
        : '',
      min: numOrNull(f.min),
      max: numOrNull(f.max),
      step: numOrNull(f.step),
      // 🔴 The row's PRESENTATION hints, carried whole and never inspected — labelColor / labelFontSize /
      //    placeholderColor / noteFontSize / … The app's style vocabulary is ~100 optional keys that grow with
      //    every redesign (docs: "Tax Planner card payloads"), and this mapper is a CLOSED rebuild: naming them
      //    one by one here would drop each new hint on the next save, silently, exactly as the note above warns.
      //    One opaque bag is the only shape that survives a vocabulary nobody in this repo owns.
      style: plainObject(f.style),
    }, f, SHORT_FORM_ROW_KEYS));
  });

  // 🔴 Known keys FIRST and the unknown ones appended, so the document keeps the shape a human reads in a diff —
  //    envelope, copy, then whatever the author added — instead of opening with a key nobody recognises.
  return carryUnknown({
    cardType: trimOnly(src.cardType) || SHORT_FORM_DEFAULTS.cardType,
    refId: trimOnly(src.refId) || SHORT_FORM_DEFAULTS.refId,
    version: Number.isFinite(version) && version > 0 ? version : SHORT_FORM_DEFAULTS.version,
    title: langText(src.title ?? src.titleTh),
    subtitle: langText(src.subtitle ?? src.subtitleTh),
    submitLabel: langText(src.submitLabel) || SHORT_FORM_DEFAULTS.submitLabel,
    // 🔴 null vs '' is load-bearing here and a bag adds a third state. null = never authored ⇒ the default
    // caption; '' = the author DELETED the decline button and it must stay deleted; a bag whose members are
    // all blank is langText's '' — i.e. the same deliberate deletion, not a reason to resurrect the button.
    secondaryLabel: src.secondaryLabel == null ? SHORT_FORM_DEFAULTS.secondaryLabel : langText(src.secondaryLabel),
    // The CARD's hints — mainFrameColor / titleColor / submitLabelFontSize / … See the row's `style` above.
    style: plainObject(src.style),
    fields,
  }, src, SHORT_FORM_CARD_KEYS);
}

/**
 * A plain JSON object, or null. The one gate every presentation-hint bag passes through on its way in.
 *
 * 🔴 It checks the SHAPE and nothing else. A hint's key and value belong to the mobile app's contract, not to
 * this file, so validating a colour here would only be a second opinion that goes stale — the backend carries
 * hints verbatim for the same reason (`TaxCardStyleV2`), and the app drops what it cannot parse. What IS
 * rejected is a bag that is not an object at all (a string, an array, a leftover from an older shape), because
 * that cannot be merged onto a card by anyone and would ride along forever once saved.
 */
function plainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

/** An empty-but-complete config (every key present, `fields` an array) — what the dialog binds to for a new card. */
function blankShortFormConfig() {
  return {
    cardType: SHORT_FORM_DEFAULTS.cardType,
    refId: SHORT_FORM_DEFAULTS.refId,
    version: SHORT_FORM_DEFAULTS.version,
    title: '',
    subtitle: '',
    submitLabel: SHORT_FORM_DEFAULTS.submitLabel,
    secondaryLabel: SHORT_FORM_DEFAULTS.secondaryLabel,
    style: null,
    fields: [],
  };
}

/**
 * The member FieldKeys of a SHORT_FORM item, in display order (trimmed, de-duped). Empty for every other kind and
 * for a SHORT_FORM whose card has no rows yet — so callers can ask any item without a kind check.
 * @param {?Object} item
 * @returns {string[]}
 */
export function shortFormFieldKeys(item) {
  if (!item || trimUpper(item.itemKind) !== 'SHORT_FORM') return [];
  const cfg = item.shortFormConfig;
  if (cfg == null || typeof cfg !== 'object' || !Array.isArray(cfg.fields)) return [];
  // 🔴 A TICK ROW WITH `options` ANSWERS NOTHING OF ITS OWN — each option carries its own FieldKey and the
  //    row's key is only the group's id (mirrors `TaxShortFormCardV2.MemberFieldKeys`, which every backend
  //    layer asks the same question of: the submit fence, the field catalog, the orphan-key check). Reading
  //    the row key here instead would name a key no box on the card can ever answer, and hide the four that
  //    can — which is what makes the save diff read a config edit as "delete this card and add another".
  const answering = cfg.fields.reduce((all, f) => {
    if (!f || typeof f !== 'object') return all;
    const options = Array.isArray(f.options)
      ? f.options.filter((o) => o && typeof o === 'object' && String(o.fieldKey || '').trim())
      : [];
    return all.concat(options.length ? options : [f]);
  }, []);
  return uniqueStrings(answering, (f) => (f && typeof f === 'object' ? f.fieldKey : null));
}

/**
 * Config → save payload. Normalize, then drop the rows the author never finished: a row with no FieldKey names no
 * sibling item and would ship as an input the app cannot key a value on. The backend rejects it outright
 * ("ช่องกรอกลำดับที่ N ยังไม่ได้เลือก fieldKey"), which would fail the whole version-wide bulk save over one
 * half-typed row — dropping it here keeps a stray blank row from blocking everything else the user just edited.
 */
function buildShortFormConfigRequest(raw) {
  const cfg = normalizeShortFormConfig(raw);
  cfg.fields = cfg.fields.filter((f) => f.fieldKey);
  // Blank note → null: the app prints the note box whenever the key is present, so '' would draw an empty one.
  // Same three states for the row's unit: '' would print an empty suffix box next to the number instead of none,
  // and a CHECKBOX row must ship no `unitLabel` key at all.
  cfg.fields.forEach((f) => {
    f.noteBelow = nzLang(f.noteBelow);
    // Same three states as `noteBelow`: '' would put an empty caption slot above the box.
    f.noteAbove = nzLang(f.noteAbove);
    f.unitLabel = nzLang(f.unitLabel);
  });
  // A money box carries NO control keys at all — sending control:'' plus three nulls would store four keys per row
  // that mean "this is not a special control", which the wire already says by their absence. Bounds belong to the
  // stepper alone: a checkbox has no range, and keeping one would draw a control nobody authored.
  cfg.fields.forEach((f) => {
    if (f.control !== 'STEPPER') {
      f.min = null;
      f.max = null;
      f.step = null;
    }
    if (!SHORT_FORM_CONTROLS.includes(f.control)) f.control = null;
  });
  return cfg;
}

/**
 * The built config → its THAI READING: every copy seat collapsed from a language bag `{th,en,zh}` to its `th`
 * member, mirroring the backend's own `TaxCardLangV2.Collapse(bag, MemberTh)`.
 *
 * 🔴 This is what the TYPED request member must carry, and sending the bag-bearing document there instead is a
 * 400 the request never survives: `TaxShortFormConfigV2Dto` declares `title` / `subtitle` / `submitLabel` /
 * `secondaryLabel` and each row's `label` / `placeholder` / `note` / `unitLabel` as `string?`, and
 * System.Text.Json cannot bind a JSON object to one — it fails during MODEL BINDING, before a line of the
 * backend's own bag handling ever runs ("The JSON value could not be converted to System.String. Path:
 * $.items[86].shortFormConfig.title"). One translated mini-form therefore took down the whole version-wide bulk
 * save. The translations are not lost by collapsing here: they ride in `shortFormConfigJson`, which is the member
 * the backend PREFERS and stores verbatim. This mirrors the GET side exactly — the API already returns the typed
 * `shortFormConfig` as the Thai reading (`TaxQuestionItemV2BAL.DeserializeShortFormConfig` collapses first) next
 * to the raw document, so the round trip now sends back the same two shapes it received.
 *
 * 🔴 Does not mutate its argument: the caller stringifies the ORIGINAL for `shortFormConfigJson`, and collapsing
 * in place would ship Thai-only there too — i.e. delete every translation on the next save.
 * 🔴 `null` stays `null` (never ''): `secondaryLabel` uses that distinction to mean "never authored" ⇒ default
 * caption, as against '' = "the author deleted the decline button".
 */
function shortFormConfigTh(cfg) {
  const th = (v) => (v == null ? v : langTh(v));
  return {
    ...cfg,
    title: th(cfg.title),
    subtitle: th(cfg.subtitle),
    submitLabel: th(cfg.submitLabel),
    secondaryLabel: th(cfg.secondaryLabel),
    fields: (cfg.fields || []).map((f) => ({
      ...f,
      label: th(f.label),
      placeholder: th(f.placeholder),
      noteBelow: th(f.noteBelow),
      noteAbove: th(f.noteAbove),
      unitLabel: th(f.unitLabel),
      // 🔴 A TICK ROW'S OPTIONS CARRY COPY TOO, and missing them here is the same 400 the doc comment
      //    above describes — just one level deeper. `TaxShortFormOptionV2Dto.label` is a `string`, so a
      //    bag reaching it fails during MODEL BINDING and the whole bulk save is rejected with a path
      //    nobody can act on ("$.items[87].shortFormConfig.fields[0].options[0].label"). Only mapped when
      //    the row actually has options, so a row without them is left byte-identical.
      ...(Array.isArray(f.options)
        ? { options: f.options.map((o) => ({ ...o, label: th(o.label) })) }
        : {}),
    })),
  };
}

/**
 * Map a TaxQuestionItemV2Dto (camelCase) → UI item (camelCase + `_uid`).
 * @param {Object} dto
 * @returns {Object}
 */
function mapItemDtoToUi(dto) {
  return {
    _uid: nextUid(),
    questionItemID: dto.questionItemID ?? null,
    questionVersionID: dto.questionVersionID ?? null,
    itemKind: trimUpper(dto.itemKind) || 'FIELD_INPUT',
    fieldKey: dto.fieldKey ?? null,
    questionTextTh: dto.questionTextTh ?? '',
    label: dto.label || '',
    helpText: dto.helpText ?? '',
    // layered prompts
    questionSystemPrompt: dto.questionSystemPrompt ?? '',
    answerSystemPrompt: dto.answerSystemPrompt ?? '',
    inputType: dto.inputType ?? null,
    answerType: dto.answerType ?? null,
    // quick-reply chips (preserve string[] or [{label,value}]) + prefer-config toggle
    answerOptions: Array.isArray(dto.answerOptions)
      ? dto.answerOptions.map((o) => (o && typeof o === 'object' ? { ...o } : o))
      : [],
    // 🔴 `!== false`, NOT Boolean(): the toggle is OPT-IN since 2026-08-12 and the stored value for "call the
    //    LLM" is an explicit false. Boolean(null) is false, so the old mapping showed every UNSET question as
    //    opted IN — the exact default the owner asked to remove — and the first save would have written it in.
    quickReplyPreferConfig: dto.quickReplyPreferConfig !== false,
    // SUMMARY card config — raw JSON strings; the dialog edits them as text, the backend validates them on save.
    summaryTemplateJson: dto.summaryTemplateJson ?? '',
    cardConfigJson: dto.cardConfigJson ?? '',
    // SHORT_FORM card template — same shape as summaryTemplateJson: a raw JSON string, opaque to the store.
    // '' = no custom template (the card renders from the built-in structure).
    shortFormTemplateJson: dto.shortFormTemplateJson ?? '',
    // FILING_CHOICE card template — the third template box, same contract as the two above: a raw JSON
    // string the store never reads into, '' = the card renders from the built-in structure. 🔴 `?? ''` and
    // NOTHING else — no trim, no parse, no normalize. The author's document (language bags, unknown keys,
    // their own indentation) is what has to come back into the textarea; anything this side "tidies" is a
    // difference the next save writes to the database without the author ever seeing it happen.
    filingTemplateJson: dto.filingTemplateJson ?? '',
    // SIMULATE card template — the FOURTH and last template box, same contract as the three above. 🔴 `?? ''`
    // and nothing else, for the reason this box exists at all: before it, an author's hand-written key had no
    // column to live in and was dropped at render, so anything this side "tidies" repeats that failure one
    // layer up.
    simulationTemplateJson: dto.simulationTemplateJson ?? '',
    // Named save-slot (SAVE_STATE writes it, SUMMARY/SIMULATE read it back) — see STATECODE_KINDS above.
    // 🔴 Same closed-triangle rule as every other kind-gated field on this DTO: dropping this line loses the
    //    value on the very next reload, well before anyone notices it never made it to the database.
    stateCode: dto.stateCode ?? '',
    // ── Resume stage 2 (SAVE_STATE only — see STATE_CONFIRM_KINDS above) ────────────────────────────
    // 🔴 Same closed-triangle rule as `stateCode` right above: an unmapped DTO member is DROPPED here, and the
    //    next save writes that loss back — the pin would quietly fall back to the old one-beat resume, and the
    //    authored script would be gone from the database before anyone noticed the screen changed.
    confirmTextTh: dto.confirmTextTh ?? '',
    // Stage-2 chips. SAME wire shape as `answerOptions` (string[] or [{label,labelEn,labelZh}]), preserved
    // verbatim for the same reason. 🔴 ORDER IS MEANING here: [0] = "ขอแก้ / อัปเดตข้อมูล" · [1] = "ใช้ข้อมูล
    //    เดิมต่อเลย". The runtime reads the decision off the POSITION, so re-ordering them swaps the outcomes.
    confirmOptions: Array.isArray(dto.confirmOptions)
      ? dto.confirmOptions.map((o) => (o && typeof o === 'object' ? { ...o } : o))
      : [],
    // Blank ⇒ the runtime falls back to THIS item's `questionSystemPrompt` (it is an instruction for wording /
    // translating the stage-2 bubble, never a rule for extracting an answer — stage 2 stores nothing).
    confirmSystemPrompt: dto.confirmSystemPrompt ?? '',
    // Ticked ⇒ stage 2 does not wait: the walk goes on in the same turn and the question behind the pin is asked
    // with THIS copy and THESE chips (one screen instead of two identical confirmations). Same closed-triangle
    // rule as the three above — dropping it here would untick it on the next save.
    confirmSkipToQuestion: dto.confirmSkipToQuestion === true,
    // Ticked ⇒ this pin is the scene a post-completion edit round returns to: a customer who asks to change an
    // answer on a FINISHED plan gets that one question reopened, then the walk comes back here (the scene's cards
    // + every item after the pin) without re-asking anything in between. At most one pin per question set.
    // Same closed-triangle rule as `confirmSkipToQuestion` right above — an older DTO without it reads as false.
    postCompleteEditReturn: dto.postCompleteEditReturn === true,
    // «คำที่ลูกค้าใช้เรียกข้อนี้ (จับคำขอแก้)» (EDIT_KEYWORD_KINDS only). Same closed-triangle rule: an unmapped field
    // is dropped here and the next save writes `[]` back. An older DTO without it reads as no keywords.
    editKeywords: Array.isArray(dto.editKeywords) ? dto.editKeywords.slice() : [],
    // App Bar group state (SAVE_STATE only — see STATEGROUP_KINDS). Same closed-triangle rule as the fields above.
    stateGroup: dto.stateGroup ?? '',
    // CALCULATE config — typed object (not a JSON string): which calc version + which formulas, in run order.
    // Always normalized so the dialog can bind straight to `.functions` on rows saved before this kind existed.
    calcConfig: normalizeCalcConfig(dto.calcConfig),
    // SHORT_FORM config — typed object: the mini-form card's captions + its input rows.
    // 🔴 An unmapped DTO field is DROPPED here, and the next save then writes that loss back: forgetting this line
    //    means the card looks fine until the builder is reloaded, at which point the config is gone and the first
    //    save after the reload deletes it in the database too.
    // 🔴 The RAW document wins on load. `dto.shortFormConfig` is the backend's TYPED reading, which is Thai
    // only — loading from it and saving back would send Thai-only and delete every translation, which is the
    // exact round trip that made bags impossible before. normalizeShortFormConfig parses a string for us.
    shortFormConfig: normalizeShortFormConfig(dto.shortFormConfigJson || dto.shortFormConfig),
    // 🔴 Every member below has to be read back, for the reason spelled out on `shortFormConfig` above: an unmapped
    //    DTO field is dropped here, and the next save writes that loss back. For this catalog the loss is every
    //    section's fan-out target — the income conversation would have nowhere to write — and it would look fine
    //    right up until the builder was reloaded.
    incomeCategories: Array.isArray(dto.incomeCategories)
      ? dto.incomeCategories.map((c) => ({
        code: c.code || '',
        labelTh: c.labelTh || '',
        flagKey: c.flagKey || '',
        keywords: Array.isArray(c.keywords) ? c.keywords.slice() : [],
        // INCOME_CLASSIFY wiring. Absent on an INCOME_CLASSIFIER catalog (the backend omits nulls), so each one
        // defaults to the blank the dialog binds to.
        incomeFieldKey: c.incomeFieldKey ?? '',
        periodFieldKey: c.periodFieldKey ?? '',
        subTypes: Array.isArray(c.subTypes)
          ? c.subTypes.map((s) => ({ code: (s && s.code) || '', labelTh: (s && s.labelTh) || '' }))
          : [],
        metadata: c.metadata
          ? {
            assetCategory: c.metadata.assetCategory ?? null,
            propertyType: c.metadata.propertyType ?? null,
            professionType: c.metadata.professionType ?? null,
            businessType: c.metadata.businessType ?? null,
          }
          : null,
      }))
      : [],
    // Knowledge, one list per LLM op — the ask op and the extract op want different documents (wording a
    // question vs decoding an answer), so they are authored separately. `questionKnowledgeQuery` is the text
    // the ask op embeds to FIND them; blank ⇒ the backend falls back to the question itself.
    questionKnowledgeIds: Array.isArray(dto.questionKnowledgeIds) ? dto.questionKnowledgeIds.slice() : [],
    answerKnowledgeIds: Array.isArray(dto.answerKnowledgeIds) ? dto.answerKnowledgeIds.slice() : [],
    questionKnowledgeQuery: dto.questionKnowledgeQuery ?? '',
    visibleWhen: dto.visibleWhen || 'Always',
    isRequired: Boolean(dto.isRequired),
    groupCode: dto.groupCode ?? null,
    sortOrder: dto.sortOrder ?? 0,
    isActive: dto.isActive !== false,
  };
}

/**
 * Map a UI item → CreateTaxQuestionItemV2Request (camelCase) for create / bulk.
 * Applies the naming canon (groupCode UPPER, fieldKey/flagKey trim-only) and gates
 * the binding by itemKind.
 * @param {Object} item
 * @param {number} [sortOrder]
 * @returns {Object}
 */
function buildItemRequest(item, sortOrder) {
  const kind = trimUpper(item.itemKind) || 'FIELD_INPUT';
  // 🔴 BOTH income kinds, and the difference between them is only whether the mini section rides along.
  //    Testing `=== 'INCOME_CLASSIFIER'` here is what would send `incomeCategories: null` for an INCOME_CLASSIFY —
  //    a save that silently deletes the eight sections the driver needs to ask anything at all.
  const carriesIncomeCatalog = INCOME_CATALOG_KINDS.includes(kind);
  const isIncomeClassify = kind === 'INCOME_CLASSIFY';
  const usesFieldKey = FIELDKEY_KINDS.includes(kind);
  const isScalar = SCALAR_KINDS.includes(kind);

  const inputType = isScalar && INPUT_TYPES.includes(item.inputType) ? item.inputType : null;
  const answerType = isScalar && ANSWER_TYPES.includes(item.answerType) ? item.answerType : null;

  const answerOptions = normalizeAnswerOptions(item.answerOptions);
  const incomeCategories = carriesIncomeCatalog
    ? mapIncomeCategories(item.incomeCategories, isIncomeClassify)
    : null;
  // SUMMARY-only config. Nulled for every other kind for the same reason incomeCategories is: config that outlives
  // its ItemKind ships a card nobody asked for the next time someone flips the kind back.
  const isSummary = kind === 'SUMMARY';
  // CALCULATE-only config, nulled for every other kind for that same reason — a stale calcConfig left behind by a
  // kind flip would re-run someone else's formulas the moment the kind flipped back.
  const isCalculate = kind === 'CALCULATE';
  // SHORT_FORM-only config, nulled elsewhere for that same reason: a mini-form config surviving on an item that is
  // no longer a SHORT_FORM would put a card in front of the customer the walker has no branch for, and its member
  // FieldKeys would keep feeding the orphan-key check for a card nobody renders.
  const isShortForm = kind === 'SHORT_FORM';
  // FILING_CHOICE-only card template, nulled elsewhere for that same reason: a template surviving a kind
  // flip would keep shaping a card this item no longer draws.
  const isFilingChoice = kind === 'FILING_CHOICE';
  // SIMULATE-only card template, nulled elsewhere for the same reason.
  const isSimulate = kind === 'SIMULATE';
  // 🔴 Built ONCE and shipped in two shapes. Building it twice let the two members drift apart for free; building
  //    it once makes it obvious that `shortFormConfigJson` is the authored document and the typed member below is
  //    only its Thai reading.
  const shortFormCfg = isShortForm ? buildShortFormConfigRequest(item.shortFormConfig) : null;

  return {
    // 🔴 Identity, so the server can UPDATE this row instead of dropping the whole version and re-inserting it.
    //    Null/absent = a genuinely new item. Without it every save regenerated all 84 QuestionItemIDs, which
    //    orphans every answer row that referenced them and churns the ids in every log and bug report.
    questionItemID: Number(item.questionItemID) > 0 ? Number(item.questionItemID) : null,
    itemKind: kind,
    // fieldKey verbatim (trim only — never uppercased); FIELD_INPUT binds one registry
    // FieldKey, INCOME_CLASSIFIER/CONFIRM carry a stable key of their own.
    fieldKey: usesFieldKey ? (trimOnly(item.fieldKey) || null) : null,
    questionTextTh: nz(item.questionTextTh),
    label: trimOnly(item.label),
    helpText: nz(item.helpText),
    questionSystemPrompt: nz(item.questionSystemPrompt),
    answerSystemPrompt: nz(item.answerSystemPrompt),
    inputType,
    answerType,
    answerOptions,
    summaryTemplateJson: isSummary ? nz(item.summaryTemplateJson) : null,
    // 🔴 Opaque string ONLY — never route this through `buildShortFormConfigRequest` /
    //    `normalizeShortFormConfig`. Those two are the CLOSED whitelist that builds `shortFormConfig` /
    //    `shortFormConfigJson` further below: they rebuild the config object key-by-key from a known list, so
    //    any key an admin hand-authors in the template that the whitelist doesn't recognize gets silently
    //    dropped on save — defeating the whole point of a custom template. `nz()` (trim → null if blank) is
    //    the entire treatment, same as `summaryTemplateJson` right above.
    shortFormTemplateJson: isShortForm ? nz(item.shortFormTemplateJson) : null,
    // 🔴 Opaque string ONLY, for the very reason this lane exists at all. FILING_CHOICE's card used to be
    //    assembled by a C# class with fixed members, so every key an admin added that the class did not
    //    declare was dropped on the floor — the template box is the answer to that, and routing it through
    //    any normalizer/whitelist HERE would re-create the same silent drop one layer up. `nz()` (trim →
    //    null if blank) is the entire treatment, same as the two template members above; blank is not an
    //    error, it means "use the built-in structure".
    filingTemplateJson: isFilingChoice ? nz(item.filingTemplateJson) : null,
    // 🔴 Opaque string ONLY, and on this kind that is the whole point: SIMULATE's card used to be assembled
    //    from `cardConfigJson` deserialized into a C# DTO with fixed members, so every key an admin added
    //    that the DTO did not declare was dropped at render — after saving cleanly and after coming back in
    //    the textarea. `nz()` (trim → null if blank) is the entire treatment; blank means "use the built-in
    //    structure", never "no card".
    simulationTemplateJson: isSimulate ? nz(item.simulationTemplateJson) : null,
    // 🔴 THREE kinds share this column, not one. SUMMARY's is the card catalog; FILING_CHOICE's is COPY
    //    (labels + badge) and SIMULATE's is MANDATORY (which deductions may be slid, which rows are compared).
    //    Nulling it for the other two silently wiped an admin's authored labels and made every SIMULATE save
    //    fail on the backend's own "SIMULATE: ต้องระบุ cardConfig".
    cardConfigJson: CARDCONFIG_KINDS.includes(kind) ? nz(item.cardConfigJson) : null,
    calcConfig: isCalculate ? normalizeCalcConfig(item.calcConfig) : null,
    // 🔴 THE THAI READING, never the authored document. The typed member cannot carry a language bag — the
    //    backend binds each copy seat to `string?`, and a JSON object there does not bind at all but THROWS in
    //    model binding, 400-ing the entire bulk save over one translated card. See `shortFormConfigTh`.
    shortFormConfig: shortFormCfg ? shortFormConfigTh(shortFormCfg) : null,
    // 🔴 …and the SAME config as TEXT, bags and all. This is the member that keeps a translated mini-form
    // translated: the backend PREFERS it when present, validates it in its Thai reading, and stores it verbatim.
    // The typed member above stays for every older client and every internal reader.
    shortFormConfigJson: shortFormCfg ? JSON.stringify(shortFormCfg) : null,
    // Named save-slot — see STATECODE_KINDS. Nulled for every other kind for the same reason cardConfigJson /
    // calcConfig are: a stale stateCode surviving a kind flip would point a later SUMMARY/SIMULATE at a
    // snapshot slot nobody authored on THIS item.
    stateCode: STATECODE_KINDS.includes(kind) ? nz(item.stateCode) : null,
    // Resume stage-2 copy — see STATE_CONFIRM_KINDS. NULLed for every other kind for the same reason
    // `stateCode` / `cardConfigJson` are: a script left behind by a kind flip is not dead config, it is a screen
    // the customer would be shown again the moment the kind flipped back.
    confirmTextTh: STATE_CONFIRM_KINDS.includes(kind) ? nz(item.confirmTextTh) : null,
    // 🔴 THE SAME normalizer as the quick-reply chips, on purpose: these rows are read by the same chip pipeline
    //    (bare strings promoted to `{label}`, `value` never synthesised, unknown keys such as `labelEn`/`labelZh`
    //    preserved). Building a second normalizer here is how the two lists would drift into two shapes.
    confirmOptions: STATE_CONFIRM_KINDS.includes(kind) ? normalizeAnswerOptions(item.confirmOptions) : null,
    confirmSystemPrompt: STATE_CONFIRM_KINDS.includes(kind) ? nz(item.confirmSystemPrompt) : null,
    // 🔴 false, not null, for every other kind: the column is NOT NULL on the table and the backend fences it the
    //    same way — a kind flip must clear the tick, never send a null the model binder reads as "unchanged".
    confirmSkipToQuestion: STATE_CONFIRM_KINDS.includes(kind) && item.confirmSkipToQuestion === true,
    // «หลังแก้แผนที่จบแล้ว ให้กลับมาที่ฉากนี้» — SAME fence and SAME reason as `confirmSkipToQuestion` right above:
    // false, not null (NOT NULL column), and a kind flip clears the tick. The backend rejects a second ticked pin
    // in the same bulk save.
    postCompleteEditReturn: STATE_CONFIRM_KINDS.includes(kind) && item.postCompleteEditReturn === true,
    // «คำที่ลูกค้าใช้เรียกข้อนี้ (จับคำขอแก้)» — see EDIT_KEYWORD_KINDS. Always an array (shape of
    // `questionKnowledgeIds`): trimmed + deduped here, `[]` for every other kind so a kind flip clears it; the
    // backend drops words shorter than 2 characters, dedupes case-insensitively and stores an empty list as NULL.
    editKeywords: EDIT_KEYWORD_KINDS.includes(kind) ? uniqueStrings(item.editKeywords, (v) => String(v ?? '')) : [],
    // App Bar group state — see STATEGROUP_KINDS. Blank → null (= this pin leaves the form's group alone); the
    // backend stores its canonical spelling and 400s a value outside the list.
    stateGroup: STATEGROUP_KINDS.includes(kind) ? nz(item.stateGroup) : null,
    quickReplyPreferConfig: Boolean(item.quickReplyPreferConfig),
    incomeCategories,
    questionKnowledgeIds: uniqueStrings(item.questionKnowledgeIds, (v) => String(v ?? '')),
    answerKnowledgeIds: uniqueStrings(item.answerKnowledgeIds, (v) => String(v ?? '')),
    questionKnowledgeQuery: nz(item.questionKnowledgeQuery),
    // stored raw (fail-open); BAL coerces blank → Always
    visibleWhen: trimOnly(item.visibleWhen) || 'Always',
    // 🔴 None of the non-askable kinds can be "required": a required item the walker skips silently would sit
    //    unanswered in the progress denominator forever and the form would never read as complete. The backend
    //    does not tolerate it either — SUMMARY / CALCULATE each throw on `isRequired`, so leaving a
    //    stale toggle in the payload turns the whole version-wide bulk save into a 400.
    isRequired: !NON_ASKABLE_KINDS.includes(kind) && Boolean(item.isRequired),
    groupCode: item.groupCode ? trimUpper(item.groupCode) : null,
    sortOrder: sortOrder != null ? Number(sortOrder) : (Number(item.sortOrder) || 0),
    isActive: item.isActive !== false,
  };
}

// ── step / section metadata ─────────────────────────────────────────────────
function blankMeta(code, label) {
  return {
    questionStepID: null,
    stepCode: code,
    label: label || groupLabel(code),
    answerSystemPrompt: '',
    questionSystemPrompt: '',
    questionKnowledgeIds: [],
    answerKnowledgeIds: [],
    questionKnowledgeCategories: [],
    answerKnowledgeCategories: [],
    questionKnowledgeQuery: '',
    sortOrder: 0,
    isActive: true,
  };
}

/**
 * Build the bulk-save `steps[]` payload from `groupMeta`, ordered by the given
 * group codes (builder display order). Skips the synthetic `GENERAL` bucket.
 * @param {string[]} [groupCodesInOrder]
 * @returns {Object[]}
 */
function buildSteps(groupCodesInOrder) {
  const codes = Array.isArray(groupCodesInOrder) && groupCodesInOrder.length
    ? groupCodesInOrder
    : Object.keys(state.groupMeta);
  const seen = new Set();
  const steps = [];
  codes.forEach((raw) => {
    const code = trimUpper(raw);
    if (!code || code === 'GENERAL' || seen.has(code)) return;
    seen.add(code);
    const meta = state.groupMeta[code] || {};
    steps.push({
      stepCode: code,
      label: trimOnly(meta.label) || code,
      answerSystemPrompt: nz(meta.answerSystemPrompt),
      questionSystemPrompt: nz(meta.questionSystemPrompt),
      questionKnowledgeIds: uniqueStrings(meta.questionKnowledgeIds, (v) => String(v ?? '')),
      answerKnowledgeIds: uniqueStrings(meta.answerKnowledgeIds, (v) => String(v ?? '')),
      questionKnowledgeCategories: uniqueStrings(meta.questionKnowledgeCategories, (v) => String(v ?? '')),
      answerKnowledgeCategories: uniqueStrings(meta.answerKnowledgeCategories, (v) => String(v ?? '')),
      questionKnowledgeQuery: nz(meta.questionKnowledgeQuery),
      sortOrder: steps.length + 1,
      isActive: meta.isActive !== false,
    });
  });
  return steps;
}

/** Get-or-create the metadata bucket for a group/step (reactive). */
function ensureGroupMeta(groupCode, label) {
  const code = trimUpper(groupCode);
  if (!code) return null;
  if (!state.groupMeta[code]) state.groupMeta[code] = blankMeta(code, label);
  return state.groupMeta[code];
}

/** Ensure metadata buckets exist for every supplied group code. */
function ensureGroupMetaForCodes(codes) {
  (codes || []).forEach((code) => ensureGroupMeta(code));
}

/** Pure lookup of a group/step metadata bucket (no mutation). */
function groupMetaFor(groupCode) {
  return state.groupMeta[trimUpper(groupCode)] || null;
}

/** Patch one or more step-level metadata fields for a group (creates if needed). */
function updateGroupMeta(groupCode, patch) {
  const meta = ensureGroupMeta(groupCode);
  if (meta && patch && typeof patch === 'object') Object.assign(meta, patch);
  return meta;
}

const GROUP_KNOWLEDGE_FIELDS = [
  'questionKnowledgeIds',
  'answerKnowledgeIds',
  'questionKnowledgeCategories',
  'answerKnowledgeCategories',
];

/**
 * Set one of the step-level knowledge lists for a group.
 * @param {string} groupCode
 * @param {'questionKnowledgeIds'|'answerKnowledgeIds'|'questionKnowledgeCategories'|'answerKnowledgeCategories'} field which op's list
 * @param {string[]} values
 */
function setGroupKnowledge(groupCode, field, values) {
  const meta = ensureGroupMeta(groupCode);
  if (!meta) return;
  // Guarded rather than trusting the caller: a typo'd field name would otherwise add a phantom key that
  // buildSteps never reads, so the picker would look like it saved and quietly drop every selection.
  if (!GROUP_KNOWLEDGE_FIELDS.includes(field)) return;
  meta[field] = Array.isArray(values) ? values.slice() : [];
}

/**
 * Generate a unique opaque StepCode for a new free-form Section (`SEC_` + short
 * suffix, UPPER). The Section's Thai name lives in `Label`. Uniqueness is enforced
 * against current groupMeta keys AND any item groupCodes.
 * @returns {string}
 */
function generateStepCode() {
  const used = new Set(Object.keys(state.groupMeta));
  state.items.forEach((it) => {
    const c = trimUpper(it.groupCode);
    if (c) used.add(c);
  });
  let code;
  do {
    const suffix = Math.random().toString(36).slice(2, 8).toUpperCase();
    code = `SEC_${suffix}`;
  } while (!code.replace('SEC_', '') || used.has(code));
  return code;
}

/**
 * Create a new free-form Section: auto-generate a unique StepCode, seed its meta
 * with the given (Thai) name, append after the existing sections.
 * @param {string} label section name (Thai)
 * @returns {string} the generated StepCode
 */
function createSection(label) {
  const code = generateStepCode();
  const maxSort = Object.values(state.groupMeta)
    .reduce((m, x) => Math.max(m, Number(x.sortOrder) || 0), 0);
  const meta = blankMeta(code, trimOnly(label) || code);
  meta.sortOrder = maxSort + 1;
  state.groupMeta[code] = meta;
  return code;
}

/** Remove a Section (its meta) and every item bound to it (by groupCode). */
function removeSection(groupCode) {
  const code = trimUpper(groupCode);
  if (!code) return;
  delete state.groupMeta[code];
  // A blank groupCode IS the synthetic GENERAL bucket (that is how the builder
  // renders it), so normalize before comparing or GENERAL items survive the delete
  // and instantly resurrect the section with no meta (= uncontrollable).
  state.items = state.items.filter((it) => (trimUpper(it.groupCode) || 'GENERAL') !== code);
}

/**
 * Create a blank UI item seeded with a kind + group, ready for the dialog.
 * @param {Object} [seed]
 * @returns {Object}
 */
function createBlankItem(seed = {}) {
  const kind = trimUpper(seed.itemKind) || 'FIELD_INPUT';
  const scalar = SCALAR_KINDS.includes(kind);
  return {
    _uid: nextUid(),
    questionItemID: null,
    questionVersionID: null,
    itemKind: kind,
    fieldKey: seed.fieldKey ?? null,
    questionTextTh: seed.questionTextTh ?? '',
    label: seed.label || '',
    helpText: seed.helpText ?? '',
    questionSystemPrompt: '',
    answerSystemPrompt: '',
    summaryTemplateJson: '',
    cardConfigJson: '',
    shortFormTemplateJson: '',
    filingTemplateJson: '',
    simulationTemplateJson: '',
    stateCode: seed.stateCode ?? '',
    // Resume stage 2 — seeded for EVERY kind (like calcConfig / shortFormConfig): the dialog binds these boxes
    // before the author has picked SAVE_STATE, and `buildItemRequest` nulls them back out for any other kind.
    confirmTextTh: seed.confirmTextTh ?? '',
    confirmOptions: [],
    confirmSystemPrompt: seed.confirmSystemPrompt ?? '',
    confirmSkipToQuestion: seed.confirmSkipToQuestion === true,
    postCompleteEditReturn: seed.postCompleteEditReturn === true,
    editKeywords: [],
    stateGroup: seed.stateGroup ?? '',
    calcConfig: normalizeCalcConfig(seed.calcConfig),
    // Seeded for EVERY kind (like calcConfig): the dialog binds the mini-form editor before the user has picked
    // SHORT_FORM, and `buildItemRequest` nulls it back out for any other kind anyway.
    shortFormConfig: normalizeShortFormConfig(seed.shortFormConfig),
    inputType: scalar ? (seed.inputType || 'text') : null,
    answerType: scalar ? (seed.answerType || 'text') : null,
    answerOptions: [],
    // Default for a NEW item = no LLM (true). Only an author flipping the toggle opts in.
    quickReplyPreferConfig: seed.quickReplyPreferConfig !== false,
    incomeCategories: [],
    questionKnowledgeIds: [],
    answerKnowledgeIds: [],
    questionKnowledgeQuery: '',
    visibleWhen: 'Always',
    isRequired: false,
    groupCode: seed.groupCode ?? null,
    sortOrder: seed.sortOrder ?? 0,
    isActive: true,
  };
}

/**
 * Load a `/full` payload into the builder cache: version, flattened ordered items
 * (UI-mapped), per-step metadata, warnings.
 * @param {Object} fullDto
 * @returns {Object[]}
 */
function loadFull(fullDto) {
  state.currentVersion = (fullDto && fullDto.version) || null;
  state.warnings = (fullDto && Array.isArray(fullDto.warnings)) ? fullDto.warnings.slice() : [];

  const meta = {};
  const steps = (fullDto && Array.isArray(fullDto.steps)) ? fullDto.steps : [];
  steps.forEach((s) => {
    const code = trimUpper(s.stepCode);
    if (!code) return;
    meta[code] = {
      questionStepID: s.questionStepID ?? null,
      stepCode: code,
      label: s.label || code,
      answerSystemPrompt: s.answerSystemPrompt ?? '',
      questionSystemPrompt: s.questionSystemPrompt ?? '',
      questionKnowledgeIds: Array.isArray(s.questionKnowledgeIds) ? s.questionKnowledgeIds.slice() : [],
      answerKnowledgeIds: Array.isArray(s.answerKnowledgeIds) ? s.answerKnowledgeIds.slice() : [],
      questionKnowledgeCategories: Array.isArray(s.questionKnowledgeCategories) ? s.questionKnowledgeCategories.slice() : [],
      answerKnowledgeCategories: Array.isArray(s.answerKnowledgeCategories) ? s.answerKnowledgeCategories.slice() : [],
      questionKnowledgeQuery: s.questionKnowledgeQuery ?? '',
      sortOrder: s.sortOrder ?? 0,
      isActive: s.isActive !== false,
    };
  });

  const groups = (fullDto && Array.isArray(fullDto.groups)) ? fullDto.groups : [];
  const items = [];
  groups.forEach((group) => {
    const code = trimUpper(group.groupCode);
    if (code && code !== 'GENERAL' && !meta[code]) {
      // Group with no backing step row — synthesize meta from the folded group fields.
      meta[code] = {
        questionStepID: null,
        stepCode: code,
        label: group.groupLabel || groupLabel(code),
        answerSystemPrompt: group.answerSystemPrompt ?? '',
        questionSystemPrompt: group.questionSystemPrompt ?? '',
        questionKnowledgeIds: Array.isArray(group.questionKnowledgeIds) ? group.questionKnowledgeIds.slice() : [],
        answerKnowledgeIds: Array.isArray(group.answerKnowledgeIds) ? group.answerKnowledgeIds.slice() : [],
        questionKnowledgeCategories: Array.isArray(group.questionKnowledgeCategories) ? group.questionKnowledgeCategories.slice() : [],
        answerKnowledgeCategories: Array.isArray(group.answerKnowledgeCategories) ? group.answerKnowledgeCategories.slice() : [],
        questionKnowledgeQuery: group.questionKnowledgeQuery ?? '',
        sortOrder: group.sortOrder ?? 0,
        isActive: true,
      };
    }
    (group.items || []).forEach((dto) => {
      const ui = mapItemDtoToUi(dto);
      if (!ui.groupCode) ui.groupCode = group.groupCode || null;
      items.push(ui);
    });
  });
  state.groupMeta = meta;
  items.sort((a, b) => (a.sortOrder - b.sortOrder) || ((a.questionItemID || 0) - (b.questionItemID || 0)));
  state.items = items;
  return items;
}

function clearBuilder() {
  state.currentVersion = null;
  state.items = [];
  state.groupMeta = {};
  state.warnings = [];
}

export const taxQuestionV2Store = {
  state,
  groupLabel,
  // UI prefs (localStorage — never part of the save payload)
  setShowCardPreview,
  // versions
  setConfigVersion,
  setVersions,
  upsertVersion,
  removeVersion,
  reflectVersionActive,
  // item mappers
  mapItemDtoToUi,
  buildItemRequest,
  normalizeCalcConfig,
  normalizeShortFormConfig,
  blankShortFormField,
  shortFormFieldKeys,
  buildSteps,
  // step meta
  ensureGroupMeta,
  ensureGroupMetaForCodes,
  groupMetaFor,
  updateGroupMeta,
  setGroupKnowledge,
  generateStepCode,
  createSection,
  removeSection,
  createBlankItem,
  loadFull,
  clearBuilder,
};

export default taxQuestionV2Store;
